/* eslint-disable no-use-before-define */
import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import classNames from 'classnames';
import styles from './styles.scss';

/*
  @actions
  the permitted values (i.e. functions) that can be mapped to a
  function in a parent component that implements ModalWrapper
  (see DeliveryDetails.jsx for an example)
*/
modalify.CLOSE = 'closeModal';
modalify.OPEN = 'openModal';

/*
  @desc: wraps any component provided in a react-modal
  @param: ModalContent - any React component that you want as a modal
  @param: mapToProps (optional) - a map of functions in ModalContent to functions on ModalWrapper
*/
function modalify(ModalContent, mapToProps = {}) {
  class ModalWrapper extends React.Component {
    constructor(props) {
      super(props);

      this.openModal = this.openModal.bind(this);
      this.closeModal = this.closeModal.bind(this);

      // if this component is uncontrolled, act accordingly
      if (props.isOpen === undefined) {
        this.state = {
          isOpen: props.isOpenOnLoad || false,
        };

        // map functions on ModalContent to functions on ModalWrapper
        this.modalProps = Object.keys(mapToProps).reduce(
          (result, key) =>
            Object.assign({}, result, {
              [key]: (data) => {
                if (this.props[key]) {
                  this.props[key](data);
                }

                this[mapToProps[key]]();
              },
            }),
          {},
        );
      }
    }

    componentDidMount() {
      Modal.setAppElement('#__next');
    }

    openModal() {
      this.setState(
        {
          isOpen: true,
        },
        () => this.props.onModalOpen(),
      );
    }

    closeModal() {
      this.setState(
        {
          isOpen: false,
        },
        () => this.props.onModalClose(),
      );
    }

    render() {
      // favour props, but fallback to internal state
      const isOpen = this.props.isOpen !== undefined ? this.props.isOpen : this.state.isOpen;

      return (
        <React.Fragment>
          {this.props.children && (
            <this.props.children.type {...this.props.children.props} onClick={this.openModal}>
              {this.props.children.props.children}
            </this.props.children.type>
          )}
          <Modal
            className={{
              base: styles.container,
              afterOpen: styles['container-after-open'],
              beforeClose: styles['container-before-close'],
            }}
            overlayClassName={{
              base: classNames(styles.overlay, {
                [styles['hide-on-mobile']]: this.props.hideOnMobile,
                [styles['hide-on-desktop']]: this.props.hideOnDesktop,
                [styles['bottom-sheet']]: this.props.isBottom,
              }),
              afterOpen: styles['overlay-after-open'],
              beforeClose: styles['overlay-before-close'],
            }}
            closeTimeoutMS={300} /* for the amination */
            isOpen={isOpen}
            onRequestClose={this.closeModal}
            contentLabel={this.props.contentLabel}
          >
            <ModalContent closeModal={this.closeModal} {...this.props} {...this.modalProps} />
          </Modal>
        </React.Fragment>
      );
    }
  }

  ModalWrapper.propTypes = {
    isOpenOnLoad: PropTypes.bool,
    isOpen: PropTypes.bool,
    onModalOpen: PropTypes.func,
    onModalClose: PropTypes.func,
    children: PropTypes.element,
    contentLabel: PropTypes.string,
    hideOnMobile: PropTypes.bool,
    hideOnDesktop: PropTypes.bool,
    isBottom: PropTypes.bool,
  };

  ModalWrapper.defaultProps = {
    isOpenOnLoad: false,
    onModalOpen: () => {},
    onModalClose: () => {},
    children: undefined,
    isOpen: undefined,
    contentLabel: null,
    hideOnMobile: false,
    hideOnDesktop: false,
    isBottom: false,
  };

  return ModalWrapper;
}

export default modalify;
